import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkIcon from '@mui/icons-material/Link';
import SyncProblemOutlinedIcon from '@mui/icons-material/SyncProblemOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { NavLink } from 'react-router-dom';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { IntlShape, useIntl } from 'react-intl';

import {
  IconButton,
  ImageThumbnail,
  MaterialAutocomplete,
  MaterialIndicator,
  SurfaceAutocomplete,
  SurfaceSelect,
  SurfaceTextField,
} from '../../../components/design-system';
import {
  useMaterialReactTableLocalization,
  useVocabulary,
} from '../../../i18n';
import { formatDate, formatDateTime } from '../../../helpers';

export type SyncFieldKey =
  | 'evId'
  | 'amcrId'
  | 'type'
  | 'material'
  | 'era'
  | 'description'
  | 'note'
  | 'foundAt';

export type SyncFields = Record<SyncFieldKey, string | null>;

interface Sources {
  acolyte: string | null;
  sheet: string | null;
  amcr: string | null;
}

type SyncSource = keyof Sources;

interface SourceOption {
  source: SyncSource;
  value: string;
}

type SourceLabels = Record<SyncSource, string>;
type ValueLabel = (field: SyncFieldKey, value: string | null) => string;
type ReasonFieldKey = SyncFieldKey | 'acolyteLink';
const dateTimeInputProps = { step: 1 };

export interface SyncOptions {
  type: string[];
  material: string[];
  era: string[];
}

export interface SyncRow {
  key: string;
  findId: string | null;
  updatedAt: string | null;
  sheetTab: string | null;
  sheetRow: number | null;
  sheetFingerprint: string | null;
  sheetLinkDiff: boolean;
  reasons: string[];
  conflict: string | null;
  fields: Record<SyncFieldKey, Sources>;
  final: SyncFields;
  image: string | null;
  images: string[];
  amcrImages: string[];
}

interface Props {
  choices: SyncOptions;
  drafts: Record<string, SyncFields>;
  loading: boolean;
  rows: SyncRow[];
  rowSelection: MRT_RowSelectionState;
  onFieldChange: (rowKey: string, field: SyncFieldKey, value: string) => void;
  onRowSelectionChange: React.Dispatch<
    React.SetStateAction<MRT_RowSelectionState>
  >;
}

const tablePaperProps = {
  elevation: 0,
  sx: { border: '1px solid', borderColor: 'softBorder' },
};
const tableContainerProps = { sx: { maxHeight: '68vh' } };
const tableBodyCellProps = {
  sx: { alignItems: 'center', verticalAlign: 'middle' },
};
const toolbarAlertBannerProps = {
  sx: {
    bgcolor: 'sunken',
    color: 'text.secondary',
    borderTop: '1px solid',
    borderBottom: '1px solid',
    borderColor: 'softBorder',
  },
};
const editorCellStyles = { width: '100%' };
const sourceSelectStyles = { minWidth: 105, flexShrink: 0 };
const valueInputStyles = { minWidth: 140, flex: 1 };
const shortValueInputStyles = { minWidth: 90, flex: 1 };
const idValueInputStyles = { minWidth: 200, flex: 1 };
const longValueInputStyles = { minWidth: 260, flex: 1 };
const imagePreviewStyles = { maxWidth: 360, maxHeight: 360, display: 'block' };
const thumbnailTriggerStyles = { display: 'inline-flex' };
const reasonStyles = { color: 'text.secondary', display: 'flex' };
const valueLabelStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
};
const sourceOptionStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0,
};
const textValueStyles = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};
const allSources: SyncSource[] = ['acolyte', 'sheet', 'amcr'];
const idSources: SyncSource[] = ['acolyte', 'sheet'];
const fieldKeys: SyncFieldKey[] = [
  'evId',
  'amcrId',
  'type',
  'material',
  'era',
  'description',
  'note',
  'foundAt',
];
const amcrFieldKeys: SyncFieldKey[] = [
  'type',
  'material',
  'era',
  'description',
  'note',
];
const acolyteFieldKeys = fieldKeys.filter((field) => field !== 'evId');
const fieldSizes: Record<SyncFieldKey, number> = {
  evId: 110,
  amcrId: 260,
  type: 180,
  material: 180,
  era: 180,
  description: 260,
  note: 260,
  foundAt: 180,
};
const editorFieldSizes: Record<SyncFieldKey, number> = {
  evId: 240,
  amcrId: 350,
  type: 300,
  material: 300,
  era: 300,
  description: 420,
  note: 420,
  foundAt: 300,
};

const renderCanonicalInput = (params: AutocompleteRenderInputParams) => (
  <SurfaceTextField {...params} size="small" />
);

const SyncValueLabel = React.memo(
  ({
    field,
    value,
    getValueLabel,
  }: {
    field: SyncFieldKey;
    value: string | null;
    getValueLabel: ValueLabel;
  }) => {
    const label = getValueLabel(field, value);

    if (field !== 'material' || !value) {
      return <>{label}</>;
    }

    return (
      <Box component="span" sx={valueLabelStyles}>
        <MaterialIndicator material={value} />
        <span>{label}</span>
      </Box>
    );
  }
);

const getChoices = (field: SyncFieldKey, choices: SyncOptions) => {
  if (field === 'type') {
    return choices.type;
  }

  if (field === 'material') {
    return choices.material;
  }

  if (field === 'era') {
    return choices.era;
  }

  return [];
};

const getSourceOptions = (row: SyncRow, field: SyncFieldKey): SourceOption[] =>
  (field === 'evId' || field === 'amcrId' ? idSources : allSources).flatMap(
    (source) => {
      const value = row.fields[field][source];

      return value == null ? [] : [{ source, value }];
    }
  );

const hasSourceSelect = (row: SyncRow, field: SyncFieldKey) =>
  new Set(getSourceOptions(row, field).map((option) => option.value)).size > 1;

const getSourceLabel = (source: string, intl: IntlShape) => {
  if (source === 'acolyte') {
    return intl.formatMessage({
      id: 'sync.source.acolyte',
      defaultMessage: 'Acolyte',
    });
  }

  if (source === 'sheet') {
    return intl.formatMessage({
      id: 'sync.source.sheet',
      defaultMessage: 'Sheet',
    });
  }

  return intl.formatMessage({ id: 'sync.source.amcr', defaultMessage: 'AMCR' });
};

const getReasonLabel = (reason: string, intl: IntlShape) => {
  if (reason === 'CREATE_ACOLYTE') {
    return intl.formatMessage({
      id: 'sync.reason.createAcolyte',
      defaultMessage: 'Will be created in Acolyte',
    });
  }

  if (reason === 'CREATE_SHEET') {
    return intl.formatMessage({
      id: 'sync.reason.createSheet',
      defaultMessage: 'Will be created in the sheet',
    });
  }

  if (reason === 'UPDATE_ACOLYTE') {
    return intl.formatMessage({
      id: 'sync.reason.updateAcolyte',
      defaultMessage: 'Acolyte can be updated',
    });
  }

  if (reason === 'UPDATE_SHEET') {
    return intl.formatMessage({
      id: 'sync.reason.updateSheet',
      defaultMessage: 'Sheet can be updated',
    });
  }

  if (reason === 'AMCR_DIFF') {
    return intl.formatMessage({
      id: 'sync.reason.amcrDiff',
      defaultMessage: 'AMCR data changed',
    });
  }

  return intl.formatMessage({
    id: 'sync.reason.conflict',
    defaultMessage: 'Identity conflict',
  });
};

const getConflictLabel = (conflict: string, intl: IntlShape) => {
  if (
    conflict === 'A new Acolyte find requires valid coordinates in the sheet.'
  ) {
    return intl.formatMessage({
      id: 'sync.conflict.missingCoordinates',
      defaultMessage:
        'A new Acolyte find requires valid coordinates in the sheet.',
    });
  }

  if (conflict === 'More than one sheet row matches this Acolyte find.') {
    return intl.formatMessage({
      id: 'sync.conflict.duplicateSheetMatch',
      defaultMessage: 'More than one sheet row matches this Acolyte find.',
    });
  }

  return conflict;
};

const getReasonFields = (reason: string, row: SyncRow): ReasonFieldKey[] => {
  if (reason === 'UPDATE_SHEET') {
    const fields = fieldKeys.filter((field) => {
      const source = row.fields[field];

      if (field === 'evId' && source.acolyte == null) {
        return false;
      }

      return source.acolyte !== source.sheet;
    }) as ReasonFieldKey[];

    return row.sheetLinkDiff ? ['acolyteLink', ...fields] : fields;
  }

  if (reason === 'UPDATE_ACOLYTE') {
    return acolyteFieldKeys.filter((field) => {
      const source = row.fields[field];

      return (
        source.acolyte == null && (source.sheet != null || source.amcr != null)
      );
    });
  }

  if (reason === 'AMCR_DIFF') {
    return amcrFieldKeys.filter((field) => {
      const values = row.fields[field];

      return (
        values.acolyte != null &&
        values.amcr != null &&
        values.acolyte !== values.amcr
      );
    });
  }

  return [];
};

const reasonIcon = (reason: string) => {
  if (reason === 'CREATE_ACOLYTE') {
    return <AddCircleOutlineIcon fontSize="small" />;
  }

  if (reason === 'CREATE_SHEET') {
    return <CloudUploadOutlinedIcon fontSize="small" />;
  }

  if (reason === 'AMCR_DIFF') {
    return <SyncProblemOutlinedIcon fontSize="small" />;
  }

  if (reason === 'CONFLICT') {
    return <WarningAmberOutlinedIcon fontSize="small" />;
  }

  return <EditOutlinedIcon fontSize="small" />;
};

const SyncThumbnail = React.memo(({ row }: { row: SyncRow }) => {
  const [open, setOpen] = useState(false);
  const imageId = row.images[0] || row.image;
  const amcrImageId = row.amcrImages[0];
  const thumbnail = imageId
    ? `https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw/${imageId}/w=80`
    : amcrImageId
    ? `https://digiarchiv.aiscr.cz/api/img/thumb?id=${amcrImageId}`
    : null;
  const preview = imageId
    ? `https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw/${imageId}/public`
    : amcrImageId
    ? `https://digiarchiv.aiscr.cz/api/img/medium?size=medium&id=${amcrImageId}`
    : null;
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  if (!thumbnail || !preview) {
    return <Typography color="text.secondary">—</Typography>;
  }

  return (
    <Tooltip
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      title={
        open ? <img alt="" src={preview} style={imagePreviewStyles} /> : ''
      }
    >
      <Box sx={thumbnailTriggerStyles}>
        <ImageThumbnail alt="" size={40} src={thumbnail} />
      </Box>
    </Tooltip>
  );
});

const SyncFieldEditor = React.memo(
  ({
    field,
    row,
    value,
    choices,
    sourceOptions,
    sourceLabels,
    chooseSourceLabel,
    getValueLabel,
    onChange,
  }: {
    field: SyncFieldKey;
    row: SyncRow;
    value: string | null;
    choices: SyncOptions;
    sourceOptions: SourceOption[];
    sourceLabels: SourceLabels;
    chooseSourceLabel: string;
    getValueLabel: ValueLabel;
    onChange: (rowKey: string, field: SyncFieldKey, value: string) => void;
  }) => {
    const values = row.fields[field];
    const editOptions = useMemo(() => {
      const base = getChoices(field, choices);
      const seen = new Set(base);
      const extra = sourceOptions
        .map((option) => option.value)
        .filter((option) => {
          if (seen.has(option)) {
            return false;
          }

          seen.add(option);

          return true;
        });

      return [...base, ...extra];
    }, [choices, field, sourceOptions]);
    const selectedSource =
      sourceOptions.find((option) => option.value === value)?.source || '';
    const handleSourceChange = useCallback(
      (event: SelectChangeEvent<unknown>) => {
        const source = String(event.target.value) as SyncSource;
        onChange(row.key, field, values[source] || '');
      },
      [field, onChange, row.key, values]
    );
    const handleValueChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) =>
        onChange(row.key, field, event.target.value),
      [field, onChange, row.key]
    );
    const handleCanonicalChange = useCallback(
      (_event: React.SyntheticEvent, nextValue: string | null) => {
        onChange(row.key, field, nextValue || '');
      },
      [field, onChange, row.key]
    );
    const getOptionLabel = useCallback(
      (option: string) => getValueLabel(field, option),
      [field, getValueLabel]
    );
    const renderSourceValue = useCallback(
      (selected: unknown) => {
        const source = String(selected) as SyncSource;

        return sourceLabels[source] || chooseSourceLabel;
      },
      [chooseSourceLabel, sourceLabels]
    );
    const inputStyles =
      field === 'description' || field === 'note'
        ? longValueInputStyles
        : field === 'amcrId'
        ? idValueInputStyles
        : field === 'evId'
        ? shortValueInputStyles
        : valueInputStyles;
    const isDateTime = field === 'foundAt' && Boolean(value?.includes('T'));
    const inputValue = isDateTime ? value?.slice(0, 19) : value;
    let inputType = 'text';
    if (field === 'foundAt') {
      inputType = isDateTime ? 'datetime-local' : 'date';
    }

    return (
      <Stack
        direction="row"
        alignItems="flex-start"
        gap={0.75}
        sx={editorCellStyles}
      >
        <SurfaceSelect
          displayEmpty
          onChange={handleSourceChange}
          renderValue={renderSourceValue}
          sx={sourceSelectStyles}
          value={selectedSource}
          size="small"
        >
          <MenuItem disabled value="">
            {chooseSourceLabel}
          </MenuItem>
          {sourceOptions.map((option) => (
            <MenuItem key={option.source} value={option.source}>
              <Box component="span" sx={sourceOptionStyles}>
                <span>{`${sourceLabels[option.source]}:`}</span>
                <SyncValueLabel
                  field={field}
                  getValueLabel={getValueLabel}
                  value={option.value}
                />
              </Box>
            </MenuItem>
          ))}
        </SurfaceSelect>
        {field === 'material' ? (
          <MaterialAutocomplete
            getOptionLabel={getOptionLabel}
            onChange={handleCanonicalChange}
            options={editOptions}
            size="small"
            surface="normal"
            sx={inputStyles}
            value={value}
          />
        ) : field === 'type' || field === 'era' ? (
          <SurfaceAutocomplete
            getOptionLabel={getOptionLabel}
            onChange={handleCanonicalChange}
            options={editOptions}
            renderInput={renderCanonicalInput}
            size="small"
            sx={inputStyles}
            value={value}
          />
        ) : (
          <SurfaceTextField
            inputProps={isDateTime ? dateTimeInputProps : undefined}
            multiline={field === 'description' || field === 'note'}
            onChange={handleValueChange}
            sx={inputStyles}
            type={inputType}
            value={inputValue || ''}
          />
        )}
      </Stack>
    );
  }
);

const SyncFieldCell = React.memo(
  ({
    field,
    row,
    value,
    choices,
    sourceLabels,
    chooseSourceLabel,
    getValueLabel,
    onChange,
  }: {
    field: SyncFieldKey;
    row: SyncRow;
    value: string | null;
    choices: SyncOptions;
    sourceLabels: SourceLabels;
    chooseSourceLabel: string;
    getValueLabel: ValueLabel;
    onChange: (rowKey: string, field: SyncFieldKey, value: string) => void;
  }) => {
    const sourceOptions = getSourceOptions(row, field);
    const distinct = new Set(sourceOptions.map((option) => option.value));

    if (distinct.size <= 1) {
      return (
        <Typography sx={textValueStyles}>
          <SyncValueLabel
            field={field}
            getValueLabel={getValueLabel}
            value={value}
          />
        </Typography>
      );
    }

    return (
      <SyncFieldEditor
        choices={choices}
        chooseSourceLabel={chooseSourceLabel}
        field={field}
        getValueLabel={getValueLabel}
        onChange={onChange}
        row={row}
        sourceLabels={sourceLabels}
        sourceOptions={sourceOptions}
        value={value}
      />
    );
  }
);

export const SyncTable = React.memo(
  ({
    choices,
    drafts,
    loading,
    rows,
    rowSelection,
    onFieldChange,
    onRowSelectionChange,
  }: Props) => {
    const intl = useIntl();
    const localization = useMaterialReactTableLocalization();
    const vocabulary = useVocabulary();
    const chooseSourceLabel = intl.formatMessage({
      id: 'sync.chooseSource',
      defaultMessage: 'Choose source',
    });
    const sourceLabels = useMemo<SourceLabels>(
      () => ({
        acolyte: getSourceLabel('acolyte', intl),
        sheet: getSourceLabel('sheet', intl),
        amcr: getSourceLabel('amcr', intl),
      }),
      [intl]
    );
    const getValueLabel = useCallback<ValueLabel>(
      (field, value) => {
        if (!value) {
          return '—';
        }

        if (field === 'foundAt') {
          const formatted = value.includes('T')
            ? formatDateTime(value)
            : formatDate(value);

          return formatted || value;
        }

        if (field === 'type') {
          return vocabulary('findType', value);
        }

        if (field === 'material' || field === 'era') {
          return vocabulary(field, value);
        }

        return value;
      },
      [vocabulary]
    );
    const sortedChoices = useMemo<SyncOptions>(() => {
      const sort = (field: SyncFieldKey, values: string[]) =>
        [...values].sort((a, b) =>
          getValueLabel(field, a).localeCompare(getValueLabel(field, b))
        );

      return {
        type: sort('type', choices.type),
        material: sort('material', choices.material),
        era: sort('era', choices.era),
      };
    }, [choices, getValueLabel]);
    const fieldLabels = useMemo<Record<SyncFieldKey, string>>(
      () => ({
        evId: intl.formatMessage({
          id: 'sync.column.sheetId',
          defaultMessage: 'Sheet ID',
        }),
        amcrId: intl.formatMessage({
          id: 'sync.column.amcrId',
          defaultMessage: 'AMCR ID',
        }),
        type: intl.formatMessage({
          id: 'sync.column.type',
          defaultMessage: 'Type',
        }),
        material: intl.formatMessage({
          id: 'sync.column.material',
          defaultMessage: 'Material',
        }),
        era: intl.formatMessage({
          id: 'sync.column.era',
          defaultMessage: 'Era',
        }),
        description: intl.formatMessage({
          id: 'sync.column.description',
          defaultMessage: 'Description',
        }),
        note: intl.formatMessage({
          id: 'sync.column.note',
          defaultMessage: 'Note',
        }),
        foundAt: intl.formatMessage({
          id: 'sync.column.foundAt',
          defaultMessage: 'Date found',
        }),
      }),
      [intl]
    );
    const acolyteLinkLabel = intl.formatMessage({
      id: 'sync.reason.acolyteLink',
      defaultMessage: 'Acolyte find ID link',
    });
    const columnSizes = useMemo(
      () =>
        Object.fromEntries(
          fieldKeys.map((field) => [
            field,
            rows.some((row) => hasSourceSelect(row, field))
              ? editorFieldSizes[field]
              : fieldSizes[field],
          ])
        ) as Record<SyncFieldKey, number>,
      [rows]
    );
    const columns = useMemo<MRT_ColumnDef<SyncRow>[]>(() => {
      const fieldColumns = fieldKeys.map(
        (field) => [field, fieldLabels[field]] as const
      );

      return [
        {
          id: 'action',
          header: intl.formatMessage({
            id: 'sync.column.action',
            defaultMessage: 'Action',
          }),
          size: 90,
          Cell: ({ row }) =>
            row.original.conflict ? (
              <Typography>—</Typography>
            ) : row.original.reasons.includes('CREATE_ACOLYTE') ? (
              <Typography>
                {intl.formatMessage({
                  id: 'sync.action.create',
                  defaultMessage: 'Create',
                })}
              </Typography>
            ) : (
              <Stack direction="row" alignItems="center">
                <Typography>
                  {intl.formatMessage({
                    id: 'sync.action.update',
                    defaultMessage: 'Update',
                  })}
                </Typography>
                {row.original.findId && (
                  <NavLink
                    to={`/f/${row.original.findId}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconButton
                      aria-label={intl.formatMessage({
                        id: 'sync.action.openFind',
                        defaultMessage: 'Open find',
                      })}
                      density="compact"
                      variant="ghost"
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </NavLink>
                )}
              </Stack>
            ),
        },
        {
          id: 'reasons',
          header: intl.formatMessage({
            id: 'sync.column.reasons',
            defaultMessage: 'Reasons',
          }),
          size: 100,
          Cell: ({ row }) => (
            <Stack direction="row" gap={0.75} sx={reasonStyles}>
              {row.original.reasons.map((reason) => {
                const fields = getReasonFields(reason, row.original);
                const label =
                  reason === 'CONFLICT' && row.original.conflict
                    ? getConflictLabel(row.original.conflict, intl)
                    : getReasonLabel(reason, intl);
                const title = fields.length
                  ? `${label}: ${fields
                      .map((field) =>
                        field === 'acolyteLink'
                          ? acolyteLinkLabel
                          : fieldLabels[field]
                      )
                      .join(', ')}`
                  : label;

                return (
                  <Tooltip key={reason} title={title}>
                    <Box component="span">{reasonIcon(reason)}</Box>
                  </Tooltip>
                );
              })}
            </Stack>
          ),
        },
        {
          id: 'image',
          header: intl.formatMessage({
            id: 'sync.column.image',
            defaultMessage: 'Image',
          }),
          size: 60,
          Cell: ({ row }) => <SyncThumbnail row={row.original} />,
        },
        ...fieldColumns.map(([field, label]) => ({
          id: field,
          header: label,
          size: columnSizes[field],
          Cell: ({ row }) => (
            <SyncFieldCell
              choices={sortedChoices}
              chooseSourceLabel={chooseSourceLabel}
              field={field}
              getValueLabel={getValueLabel}
              row={row.original}
              sourceLabels={sourceLabels}
              value={drafts[row.original.key]?.[field] || null}
              onChange={onFieldChange}
            />
          ),
        })),
      ];
    }, [
      chooseSourceLabel,
      acolyteLinkLabel,
      columnSizes,
      drafts,
      fieldLabels,
      getValueLabel,
      intl,
      onFieldChange,
      sortedChoices,
      sourceLabels,
    ]);
    const table = useMaterialReactTable({
      columns,
      data: rows,
      enableColumnActions: false,
      enableColumnFilters: false,
      enableDensityToggle: false,
      enableFullScreenToggle: false,
      enableRowSelection: (row) => !row.original.conflict,
      enableRowVirtualization: true,
      getRowId: (row) => row.key,
      localization,
      muiTableBodyCellProps: tableBodyCellProps,
      muiTableContainerProps: tableContainerProps,
      muiTablePaperProps: tablePaperProps,
      muiToolbarAlertBannerProps: toolbarAlertBannerProps,
      onRowSelectionChange,
      state: { isLoading: loading, rowSelection },
    });

    return <MaterialReactTable table={table} />;
  }
);
